import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Github, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { useAuth } from '@/hooks/useAuth';
import { authService, type OAuthProvider } from '@/services/authService';
import type { LoginFormData } from '@/types';

const loginSchema = z.object({
  email: z.string().email('올바른 이메일 주소를 입력해주세요'),
  password: z.string().min(6, '비밀번호는 최소 6자 이상이어야 합니다'),
});

export function LoginPage() {
  const { login, isLoading } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [oauthLoading, setOauthLoading] = useState<OAuthProvider | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginFormData) => {
    setError(null);
    try {
      await login(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : '로그인에 실패했습니다');
    }
  };

  const handleOAuth = async (provider: OAuthProvider) => {
    setError(null);
    setOauthLoading(provider);
    try {
      await authService.initiateOAuth(provider);
    } catch (err) {
      setError(err instanceof Error ? err.message : `${provider} 연결에 실패했습니다`);
      setOauthLoading(null);
    }
  };

  return (
    <>
      <div className="flex flex-col space-y-2 text-center">
        <h1 className="text-2xl font-semibold tracking-tight">다시 오신 것을 환영합니다</h1>
        <p className="text-sm text-muted-foreground">
          계정에 로그인하려면 정보를 입력해주세요
        </p>
      </div>

      <div className="grid gap-6">
        {/* OAuth Buttons */}
        <div className="grid gap-2">
          <Button
            variant="outline"
            type="button"
            disabled={oauthLoading !== null}
            onClick={() => handleOAuth('google')}
          >
            <Mail className="mr-2 h-4 w-4" />
            {oauthLoading === 'google' ? '연결 중...' : 'Google로 계속하기'}
          </Button>
          <Button
            variant="outline"
            type="button"
            disabled={oauthLoading !== null}
            onClick={() => handleOAuth('github')}
          >
            <Github className="mr-2 h-4 w-4" />
            {oauthLoading === 'github' ? '연결 중...' : 'GitHub로 계속하기'}
          </Button>
        </div>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <Separator className="w-full" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background px-2 text-muted-foreground">
              또는
            </span>
          </div>
        </div>

        {/* Login Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="grid gap-4">
          {error && (
            <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              {error}
            </div>
          )}

          <div className="grid gap-2">
            <Label htmlFor="email">이메일</Label>
            <Input
              id="email"
              type="email"
              placeholder="name@example.com"
              autoComplete="email"
              error={!!errors.email}
              {...register('email')}
            />
            {errors.email && (
              <p className="text-xs text-destructive">{errors.email.message}</p>
            )}
          </div>

          <div className="grid gap-2">
            <Label htmlFor="password">비밀번호</Label>
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              error={!!errors.password}
              {...register('password')}
            />
            {errors.password && (
              <p className="text-xs text-destructive">{errors.password.message}</p>
            )}
          </div>

          <Button type="submit" loading={isLoading}>
            로그인
          </Button>
        </form>

        <p className="text-center text-sm text-muted-foreground">
          계정이 없으신가요? 관리자에게 문의해주세요.
        </p>
      </div>
    </>
  );
}
